import { api } from '../api';
import { DIAS_SEMANA_NOMES, DIAS_SEMANA_ABREVIADOS } from '../../utils/dateUtils';
import { DashboardStats, ConsultaDia, TodoItem, ConsultaSemProntuario, HorariosDia } from '../../types/dashboard';

// Constantes para padronizar dias da semana (já exportadas de dateUtils)
export { DIAS_SEMANA_NOMES, DIAS_SEMANA_ABREVIADOS };

class DashboardService {
  
  // Obter estatísticas gerais do dashboard
  async obterEstatisticas(dataReferencia?: string): Promise<DashboardStats> {
    try {
      const params = dataReferencia ? `?data=${dataReferencia}` : '';
      const response = await api.get(`/dashboard/estatisticas${params}`);
      
      if (response.data.sucesso) {
        return response.data.dados;
      } else {
        throw new Error(response.data.mensagem || 'Erro ao obter estatísticas');
      }
    } catch (error) {
      console.error('[DashboardService] Erro ao obter estatísticas:', error);
      throw error;
    }
  }

  // Obter consultas de um dia específico
  async obterConsultasDoDia(data: string): Promise<ConsultaDia[]> {
    try {
      const response = await api.get(`/dashboard/consultas-dia/${data}`);
      
      if (response.data.sucesso) {
        return response.data.dados;
      } else {
        throw new Error(response.data.mensagem || 'Erro ao obter consultas do dia');
      }
    } catch (error) {
      console.error('[DashboardService] Erro ao obter consultas do dia:', error);
      throw error;
    }
  }

  // Obter consultas realizadas sem prontuário
  async obterConsultasSemProntuario(limit: number = 20): Promise<ConsultaSemProntuario[]> {
    try {
//       console.log('[DashboardService] Obtendo consultas sem prontuário, limit:', limit);
      const response = await api.get(`/dashboard/consultas-sem-prontuario?limit=${limit}`);
      
      if (response.data.sucesso) {
//         console.log('[DashboardService] Consultas sem prontuário obtidas:', response.data.dados.length);
        
        // Log detalhado das datas para debug
        if (Array.isArray(response.data.dados)) {
          response.data.dados.forEach((consulta: any, index: number) => {
//             console.log(`[DashboardService] Consulta ${index + 1} - Análise de data:`, {
//               id: consulta.id,
//               data_consulta_raw: consulta.data_consulta,
//               data_consulta_tipo: typeof consulta.data_consulta,
//               data_consulta_toString: consulta.data_consulta?.toString(),
//               nome_paciente: consulta.nome_paciente
//             });
          });
        }
        
        return response.data.dados;
      } else {
        throw new Error(response.data.mensagem || 'Erro ao obter consultas sem prontuário');
      }
    } catch (error) {
      console.error('[DashboardService] Erro ao obter consultas sem prontuário:', error);
      throw error;
    }
  }

  // Obter horários livres da semana
  async obterHorariosLivresSemana(dataInicio: string): Promise<HorariosDia[]> {
    try {
//       console.log('[DashboardService] Obtendo horários livres da semana:', {
//         dataInicio,
//         periodo: this.calcularPeriodoSemana(dataInicio)
//       });
      
      const response = await api.get(`/dashboard/horarios-livres/${dataInicio}`);
      
      if (response.data.sucesso) {
        const horariosLivres = response.data.dados;
        
//         console.log('[DashboardService] Horários livres obtidos:', horariosLivres?.length || 0, 'dias');
        
        // Verificar e garantir dias corretos - agora deve estar alinhado
        if (Array.isArray(horariosLivres)) {
          horariosLivres.forEach((dia) => {
            if (dia && dia.data) {
              const dataObj = new Date(dia.data + 'T12:00:00'); // Meio dia para evitar problemas de timezone
              const diaSemanaReal = dataObj.getDay();
              const diaSemanaEsperado = DIAS_SEMANA_NOMES.indexOf(dia.dia_semana);
              
              // Apenas corrigir se necessário, sem logs verbosos
              if (diaSemanaReal !== diaSemanaEsperado) {
//                 console.log(`[DashboardService] Corrigindo dia da semana: ${dia.dia_semana} → ${DIAS_SEMANA_NOMES[diaSemanaReal]}`);
                dia.dia_semana = DIAS_SEMANA_NOMES[diaSemanaReal];
              }
            }
          });
        }
        
        return horariosLivres || [];
      } else {
        throw new Error(response.data.mensagem || 'Erro ao obter horários livres');
      }
    } catch (error) {
      console.error('[DashboardService] Erro ao obter horários livres:', error);
      throw error;
    }
  }

  // === MÉTODOS PARA TO-DO LIST ===

  // Listar itens da to-do list
  async listarTodos(): Promise<TodoItem[]> {
    try {
      const response = await api.get('/dashboard/todo');
      
      if (response.data.sucesso) {
        return response.data.dados;
      } else {
        throw new Error(response.data.mensagem || 'Erro ao listar itens da to-do list');
      }
    } catch (error) {
      console.error('[DashboardService] Erro ao listar to-do items:', error);
      throw error;
    }
  }

  // Criar novo item na to-do list
  async criarTodo(item: Omit<TodoItem, 'id' | 'cod_empresa' | 'usuario_id'>): Promise<{ sucesso: boolean; mensagem: string; id?: number }> {
    try {
      const response = await api.post('/dashboard/todo', item);
      
      return {
        sucesso: response.data.sucesso,
        mensagem: response.data.mensagem,
        id: response.data.dados?.id
      };
    } catch (error) {
      console.error('[DashboardService] Erro ao criar todo:', error);
      return {
        sucesso: false,
        mensagem: error instanceof Error ? error.message : 'Erro desconhecido ao criar tarefa'
      };
    }
  }
  
  // Atualizar item existente
  async atualizarTodo(id: number, dados: Partial<TodoItem>): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const response = await api.put(`/dashboard/todo/${id}`, dados);
      
      return {
        sucesso: response.data.sucesso,
        mensagem: response.data.mensagem
      };
    } catch (error) {
      console.error('[DashboardService] Erro ao atualizar todo:', error);
      return {
        sucesso: false,
        mensagem: error instanceof Error ? error.message : 'Erro desconhecido ao atualizar tarefa'
      };
    }
  }
  
  // Excluir item
  async excluirTodo(id: number): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const response = await api.delete(`/dashboard/todo/${id}`);
      
      return {
        sucesso: response.data.sucesso,
        mensagem: response.data.mensagem
      };
    } catch (error) {
      console.error('[DashboardService] Erro ao excluir todo:', error);
      return {
        sucesso: false,
        mensagem: error instanceof Error ? error.message : 'Erro desconhecido ao excluir tarefa'
      };
    }
  }
  
  // Reordenar itens
  async reordenarTodos(items: { id: number; ordem: number }[]): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const response = await api.put('/dashboard/todo/reordenar', { itens: items });
      
      return {
        sucesso: response.data.sucesso,
        mensagem: response.data.mensagem
      };
    } catch (error) {
      console.error('[DashboardService] Erro ao reordenar todos:', error);
      return {
        sucesso: false,
        mensagem: error instanceof Error ? error.message : 'Erro desconhecido ao reordenar tarefas'
      };
    }
  }
  
  // === MÉTODOS UTILITÁRIOS PARA DATAS ===
  
  // Função para obter data atual no formato YYYY-MM-DD
  obterDataAtual(): string {
    return new Date().toISOString().split('T')[0];
  }
  
  // Função para obter data anterior (usada na navegação)
  obterDataAnterior(dataAtual: string): string {
//     console.log('[DashboardService] Obtendo data anterior a:', dataAtual);
    const data = new Date(dataAtual + 'T12:00:00'); // Meio dia para evitar problemas de timezone
    data.setDate(data.getDate() - 1);
    const resultado = data.toISOString().split('T')[0];
//     console.log('[DashboardService] Data anterior calculada:', resultado);
    return resultado;
  }

  // Função para obter data posterior (usada na navegação)
  obterDataPosterior(dataAtual: string): string {
//     console.log('[DashboardService] Obtendo data posterior a:', dataAtual);
    const data = new Date(dataAtual + 'T12:00:00'); // Meio dia para evitar problemas de timezone
    data.setDate(data.getDate() + 1);
    const resultado = data.toISOString().split('T')[0];
//     console.log('[DashboardService] Data posterior calculada:', resultado);
    return resultado;
  }

  // Função para obter o início da semana atual (DOMINGO da semana atual)
  obterInicioSemanaAtual(): string {
    const hoje = new Date();
    hoje.setHours(12, 0, 0, 0); // Meio dia para evitar problemas de timezone
    
    const diaSemana = hoje.getDay(); // 0 (domingo) a 6 (sábado)
    
    // CORREÇÃO DEFINITIVA: Sempre mostrar semana de DOMINGO a SÁBADO
    // Se hoje for domingo (0), usar hoje mesmo
    // Se hoje for outro dia, retroceder para o domingo da semana atual
    const diasParaDomingo = diaSemana === 0 ? 0 : -diaSemana;
    
//     console.log('[DashboardService] Calculando início da semana (DOMINGO):', {
//       data_atual: hoje.toISOString().split('T')[0],
//       dia_semana_numero: diaSemana,
//       dia_semana_nome: DIAS_SEMANA_NOMES[diaSemana],
//       dias_para_domingo: diasParaDomingo,
//       logica: 'Sempre usar DOMINGO como início da semana para incluir todos os dias'
//     });
    
    const domingo = new Date(hoje);
    domingo.setDate(hoje.getDate() + diasParaDomingo);
    
    const resultado = domingo.toISOString().split('T')[0];
    
//     console.log('[DashboardService] Início da semana calculado:', {
//       data_resultado: resultado,
//       dia_semana_resultado: domingo.getDay(),
//       confirmacao: domingo.getDay() === 0 ? 'CORRETO (Domingo)' : 'ERRO - Não é domingo!'
//     });
    
    return resultado;
  }

  // Função para obter o dia da semana de uma data (0-6)
  obterDiaSemana(data: string): number {
    if (!data) return -1;
    return new Date(data + 'T12:00:00').getDay();
  }

  // Função para obter o nome do dia da semana
  obterNomeDiaSemana(data: string): string {
    const diaSemana = this.obterDiaSemana(data);
    return diaSemana >= 0 ? DIAS_SEMANA_NOMES[diaSemana] : '';
  }

  // Função para obter a abreviação do dia da semana
  obterDiaSemanaAbreviado(data: string): string {
    const diaSemana = this.obterDiaSemana(data);
    return diaSemana >= 0 ? DIAS_SEMANA_ABREVIADOS[diaSemana] : '';
  }

  // Função para formatar data para exibição (DD/MM/YYYY)
  formatarDataExibicao(data: string): string {
    if (!data) return '';
    
    const partes = data.split('-');
    if (partes.length !== 3) return data;
    
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  }

  // Função para calcular período da semana a partir de uma data
  calcularPeriodoSemana(dataInicio: string): { inicio: string; fim: string; dias: string[] } {
    const dataObj = new Date(dataInicio + 'T12:00:00');
    
    // Gerar array com todas as datas da semana (7 dias a partir da data inicial)
    const dias = [];
    for (let i = 0; i < 7; i++) {
      const tempDate = new Date(dataObj);
      tempDate.setDate(dataObj.getDate() + i);
      dias.push(tempDate.toISOString().split('T')[0]);
    }
    
    return {
      inicio: dias[0],
      fim: dias[6],
      dias
    };
  }
  
  // Função para formatar período da semana para exibição
  formatarPeriodoSemana(dataInicio: string): string {
    const periodo = this.calcularPeriodoSemana(dataInicio);
    
    const inicioFormatado = this.formatarDataExibicao(periodo.inicio);
    const fimFormatado = this.formatarDataExibicao(periodo.fim);
    
    return `${inicioFormatado} - ${fimFormatado}`;
  }
  
  // Função para obter cor da prioridade
  obterCorPrioridade(prioridade: string): string {
    switch (prioridade) {
      case 'alta': return '#dc3545'; // vermelho
      case 'media': return '#fd7e14'; // laranja
      case 'baixa': return '#6c757d'; // cinza
      default: return '#007bff'; // azul
    }
  }
}

export const dashboardService = new DashboardService();
export type { DashboardStats, ConsultaDia, TodoItem, ConsultaSemProntuario, HorariosDia };
